/**
 * Form per la creazione di un reso su uno scontrino esistente.
 *
 * Permette di cercare lo scontrino originale, selezionare le righe
 * da rendere (reso parziale) con quantita individuale, e creare
 * lo scontrino di reso.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

interface RigaOrigine {
  id: string;
  year: number;
  descrizione: string;
  quantita: string;
  prezzo_unitario: string;
  importo_riga: string;
  articolo_id: string | null;
}

/** Stato locale per riga: selezione e quantita reso. */
interface RigaResoState {
  selezionata: boolean;
  quantitaReso: string;
}

const formatCurrency = (value: string): string =>
  `${parseFloat(value).toFixed(2)} \u20AC`;

const ResoForm: React.FC = () => {
  const navigate = useNavigate();

  const [scontrinoOrigineId, setScontrinoOrigineId] = useState('');
  const [scontrinoOrigineYear, setScontrinoOrigineYear] = useState(
    String(new Date().getFullYear()),
  );
  const [righeOrigine, setRigheOrigine] = useState<RigaOrigine[]>([]);
  const [righeStato, setRigheStato] = useState<Record<string, RigaResoState>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resoCreato, setResoCreato] = useState(false);

  const cercaScontrino = useCallback(async () => {
    if (!scontrinoOrigineId) return;
    setLoading(true);
    setError(null);
    setResoCreato(false);
    try {
      const year = Number(scontrinoOrigineYear);
      const res = await fetch(
        `/api/v1/cassa/scontrini/${scontrinoOrigineId}/righe?year=${year}`,
        { headers: authHeadersGet() },
      );
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: RigaOrigine[] = await res.json();
      const righeConYear = data.map((r) => ({ ...r, year }));
      setRigheOrigine(righeConYear);
      /* Inizializza stato: tutte selezionate, quantita = originale */
      const stato: Record<string, RigaResoState> = {};
      for (const r of righeConYear) {
        stato[r.id] = { selezionata: true, quantitaReso: r.quantita };
      }
      setRigheStato(stato);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore ricerca');
    } finally {
      setLoading(false);
    }
  }, [scontrinoOrigineId, scontrinoOrigineYear]);

  /** Toggle selezione riga. */
  const toggleRiga = (rigaId: string) => {
    setRigheStato((prev) => ({
      ...prev,
      [rigaId]: { ...prev[rigaId], selezionata: !prev[rigaId].selezionata },
    }));
  };

  /** Aggiorna quantita reso per riga. */
  const setQuantitaReso = (rigaId: string, value: string) => {
    setRigheStato((prev) => ({
      ...prev,
      [rigaId]: { ...prev[rigaId], quantitaReso: value },
    }));
  };

  const righeSelezionate = righeOrigine.filter(
    (r) => righeStato[r.id]?.selezionata,
  );

  const creaReso = async () => {
    if (righeSelezionate.length === 0) {
      setError('Seleziona almeno una riga da rendere');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      /* Cerca turno aperto */
      const turniRes = await fetch('/api/v1/cassa/turni?stato=aperto&limit=1', {
        headers: authHeadersGet(),
      });
      if (!turniRes.ok) throw new Error('Nessun turno aperto');
      const turni = await turniRes.json();
      if (turni.length === 0) throw new Error('Apri un turno prima di fare un reso');

      const turnoId = turni[0].id;
      const year = Number(scontrinoOrigineYear);

      /* Crea scontrino reso */
      const resoRes = await fetch('/api/v1/cassa/scontrini/reso', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          turno_id: turnoId,
          scontrino_origine_id: scontrinoOrigineId,
          scontrino_origine_year: year,
        }),
      });
      if (!resoRes.ok) {
        const err = await resoRes.json();
        throw new Error(err.detail ?? `Errore ${resoRes.status}`);
      }
      const resoData = await resoRes.json();

      /* Aggiungi solo le righe selezionate con la quantita specificata */
      for (const riga of righeSelezionate) {
        const stato = righeStato[riga.id];
        const rigaRes = await fetch(
          `/api/v1/cassa/scontrini/${resoData.id}/righe-reso?year=${resoData.year}`,
          {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
              riga_origine_id: riga.id,
              riga_origine_year: riga.year,
              quantita_reso: stato.quantitaReso,
            }),
          },
        );
        if (!rigaRes.ok) {
          const err = await rigaRes.json();
          throw new Error(err.detail ?? `Errore riga reso ${rigaRes.status}`);
        }
      }

      setResoCreato(true);
      navigate(`/cassa/scontrini/${resoData.id}?year=${resoData.year}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione reso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Reso merce
        </h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Ricerca scontrino */}
      <Card>
        <CardHeader>
          <CardTitle>Cerca scontrino originale</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">
                ID scontrino
              </label>
              <Input
                value={scontrinoOrigineId}
                onChange={(e) => setScontrinoOrigineId(e.target.value)}
                placeholder="UUID scontrino"
                className="w-80"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">Anno</label>
              <Input
                type="number"
                value={scontrinoOrigineYear}
                onChange={(e) => setScontrinoOrigineYear(e.target.value)}
                className="w-24"
              />
            </div>
            <Button onClick={cercaScontrino} disabled={loading || !scontrinoOrigineId}>
              Cerca
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Righe originali con selezione e quantita */}
      {righeOrigine.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Righe da rendere
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">Qt&agrave; orig.</TableHead>
                  <TableHead className="w-24 text-right">Qt&agrave; reso</TableHead>
                  <TableHead className="text-right">Prezzo</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Articolo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {righeOrigine.map((r: RigaOrigine) => {
                  const stato = righeStato[r.id];
                  return (
                    <TableRow
                      key={r.id}
                      className={stato?.selezionata ? '' : 'opacity-40'}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={stato?.selezionata ?? false}
                          onChange={() => toggleRiga(r.id)}
                          className="h-4 w-4 rounded border-border"
                        />
                      </TableCell>
                      <TableCell>{r.descrizione}</TableCell>
                      <TableCell className="text-right font-mono">
                        {Number(r.quantita)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="1"
                          max={Number(r.quantita)}
                          step="1"
                          value={stato?.quantitaReso ?? r.quantita}
                          onChange={(e) => setQuantitaReso(r.id, e.target.value)}
                          disabled={!stato?.selezionata}
                          className="w-20 text-right font-mono"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(r.prezzo_unitario)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(r.importo_riga)}
                      </TableCell>
                      <TableCell>
                        {r.articolo_id ? (
                          <Badge variant="outline">Codificato</Badge>
                        ) : (
                          <Badge variant="destructive">Aperto</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
          <div className="flex items-center justify-between p-4">
            <p className="text-sm text-muted-foreground">
              {righeSelezionate.length} di {righeOrigine.length} righe selezionate
            </p>
            <Button
              onClick={creaReso}
              disabled={loading || resoCreato || righeSelezionate.length === 0}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Crea reso {righeSelezionate.length === righeOrigine.length ? 'completo' : 'parziale'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ResoForm;
